import {HttpStatusCode} from '@angular/common/http';

/**
 * @class
 * @description This class maintains the error model for our application.
 * @export
 * @name ErrorModel
 */
export class ErrorModel {

  /**
   * @description This method creates a new instance of the error model from an error that was passed in.
   * @memberof ErrorModel
   * @method
   * @name ErrorModel.fromError
   * @param {Error} error The error that occurred.
   * @param {HttpStatusCode} httpStatusCode The HTTP status code representing the error.
   * @param {string?} summary The summary of the error that occurred.
   * @param {string?} icon The icon representing the error that occurred.
   * @returns {ErrorModel} The new instance of the error model.
   * @public
   * @static
   */
  public static fromError(error: Error, httpStatusCode: HttpStatusCode = HttpStatusCode.InternalServerError, summary?: string, icon?: string): ErrorModel {

    // Return a new instance of the error model built upon the error that was passed in.
    return new ErrorModel(error.message, httpStatusCode, error.stack?.split('\n') ?? [], summary, icon);
  }

  /**
   * @constructor
   * @description This method creates a new instance of the error model.
   * @memberof ErrorModel
   * @name ErrorModel#constructor
   * @param {string} message The description of the error that occurred.
   * @param {HttpStatusCode} code The HTTP status code representing the error.
   * @param {string[]} trace The stack trace of the error that occurred.
   * @param {string?} summary The summary of the error that occurred.
   * @param {string?} icon The icon representing the error that occurred.
   * @public
   */
  public constructor(
    /**
     * @description This property contains the description of the error that occurred.
     * @memberof ErrorModel
     * @name ErrorModel#message
     * @property
     * @public
     * @type {string}
     */
    public message: string,
    /**
     * @description This property contains the HTTP status code representing the error.
     * @memberof ErrorModel
     * @name ErrorModel#code
     * @property
     * @public
     * @type {HttpStatusCode}
     */
    public code: HttpStatusCode = HttpStatusCode.InternalServerError,
    /**
     * @description This property contains the stack trace of the error that occurred.
     * @memberof ErrorModel
     * @name ErrorModel#trace
     * @property
     * @public
     * @type {string[]}
     */
    public trace: string[] = [],
    /**
     * @description This property contains the summary of the error that occurred.
     * @memberof ErrorModel
     * @name ErrorModel#summary
     * @property
     * @public
     * @type {string?}
     */
    public summary?: string,
    /**
     * @description This property contains the icon representing the error that occurred.
     * @memberof ErrorModel
     * @name ErrorModel#icon
     * @property
     * @public
     * @type {string?}
     */
    public icon?: string,
  ) {
  }
}
